import { useState, useEffect, useCallback } from 'react';
import { authHeaders } from './listPageStyles';
import { Section, Icon } from './detail/DetailShell';

// Bildirimler sekmesi (Bölüm 7.3): Tamamlanma bildirimi + Cevaba bağlı (koşullu) bildirim.
// Değer Survey.NotificationsJson içinde tutulur; kayıt ana "Kaydet" butonuyla yapılır
// (bu bileşen yalnız form.notifications'ı onChange ile günceller).

const C = { line: '#e7e6f3', ink: '#1e1b3a', inkSoft: '#4b4670', indigo: '#4f46e5', muted: '#94a3b8' };
const CHOICE_TYPES = ['List', 'SingleChoice', 'MultiChoice'];

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(p) { return pick(p, 'Data', 'data') ?? p; }
function optionListOf(q) {
  const opts = pick(q, 'options', 'Options');
  if (Array.isArray(opts) && opts.length) return opts.map((o) => ({ id: pick(o, 'id', 'Id'), text: pick(o, 'text', 'Text', 'OptionText') || '' }));
  return [];
}

function Switch({ checked, onChange }) {
  return (
    <button type="button" onClick={() => onChange(!checked)}
      style={{ width: 40, height: 23, borderRadius: 999, border: 'none', cursor: 'pointer', background: checked ? C.indigo : '#cbd5e1', position: 'relative', flexShrink: 0 }}>
      <span style={{ position: 'absolute', top: 2, left: checked ? 19 : 2, width: 19, height: 19, borderRadius: '50%', background: '#fff', transition: 'left .15s' }} />
    </button>
  );
}

function RecipientChips({ items, onChange }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (v && !items.includes(v)) onChange([...items, v]);
    setDraft('');
  };
  return (
    <div>
      <input value={draft} onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        placeholder="E-posta yazıp Enter'a basın"
        style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid #d8d9fb`, fontSize: 13, boxSizing: 'border-box' }} />
      {items.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {items.map((it) => (
            <span key={it} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#eef2ff', color: '#3730a3', borderRadius: 999, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>
              {it}
              <button type="button" onClick={() => onChange(items.filter((x) => x !== it))} style={{ border: 'none', background: 'transparent', color: '#6366f1', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SurveyNotificationsTab({ surveyId, value, onChange, showToast }) {
  const v = value || { onCompleteEnabled: false, onCompleteRecipients: [], conditionalRules: [] };
  const [listQuestions, setListQuestions] = useState([]);

  const fetchQuestions = useCallback(async () => {
    try {
      const res = await fetch(`/api/surveys/${surveyId}/questions`, { headers: authHeaders() });
      const body = unwrap(await res.json());
      const items = (pick(body, 'items', 'Items') || [])
        .filter((q) => CHOICE_TYPES.includes(pick(q, 'questionType', 'QuestionType')))
        .map((q) => ({ id: pick(q, 'id', 'Id'), label: pick(q, 'questionText', 'QuestionText') || 'Liste sorusu', options: optionListOf(q) }));
      setListQuestions(items);
    } catch (err) { showToast?.('error', err.message || 'Sorular alınamadı'); }
  }, [surveyId, showToast]);

  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  const setOnComplete = (patch) => onChange({ ...v, ...patch });
  const setRules = (rules) => onChange({ ...v, conditionalRules: rules });

  const addRule = () => {
    const first = listQuestions[0];
    setRules([...(v.conditionalRules || []), {
      id: `r${Date.now()}`,
      questionId: first?.id || '',
      optionId: first?.options?.[0]?.id || '',
      recipients: [],
    }]);
  };
  const updateRule = (id, patch) => setRules((v.conditionalRules || []).map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const removeRule = (id) => setRules((v.conditionalRules || []).filter((r) => r.id !== id));

  return (
    <div className="dps-form">
      <Section icon="check" title="Anket tamamlanınca bildirim" subtitle="Her tamamlanan yanıtta seçilen alıcılara e-posta gönderilir">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0' }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: C.ink }}>Tamamlanma bildirimi etkin</div>
          <Switch checked={!!v.onCompleteEnabled} onChange={(b) => setOnComplete({ onCompleteEnabled: b })} />
        </div>
        {v.onCompleteEnabled && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 5 }}>Alıcılar</div>
            <RecipientChips items={v.onCompleteRecipients || []} onChange={(r) => setOnComplete({ onCompleteRecipients: r })} />
          </div>
        )}
      </Section>

      <Section icon="git-branch" title="Cevaba bağlı bildirim" subtitle="Belirli bir liste sorusuna belirli bir cevap verilince e-posta gönderilir"
        right={listQuestions.length > 0 ? (
          <button className="dps-blk-add" onClick={addRule}><Icon name="plus" size={15} /> Koşullu bildirim ekle</button>
        ) : null}
      >
        {listQuestions.length === 0 ? (
          <div style={{ fontSize: 13, color: C.muted, padding: '6px 0' }}>
            Koşullu bildirim tanımlamak için önce "Sorular" sekmesinde bir Liste sorusu ekleyin.
          </div>
        ) : (v.conditionalRules || []).length === 0 ? (
          <div style={{ fontSize: 13, color: C.muted, padding: '6px 0' }}>Henüz kural yok. "Koşullu bildirim ekle" ile başlayın.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {(v.conditionalRules || []).map((rule) => {
              const q = listQuestions.find((x) => x.id === rule.questionId) || listQuestions[0];
              const opts = (q?.options || []).filter((o) => (o.text || '').trim() !== '');
              return (
                <div key={rule.id} style={{ background: '#fafafd', border: `1px solid ${C.line}`, borderRadius: 10, padding: 14 }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                    <select value={rule.questionId} onChange={(e) => updateRule(rule.id, { questionId: e.target.value, optionId: '' })}
                      style={{ flex: '1 1 200px', padding: '8px 10px', borderRadius: 8, border: `1px solid #d8d9fb`, fontSize: 13 }}>
                      {listQuestions.map((lq) => <option key={lq.id} value={lq.id}>{lq.label}</option>)}
                    </select>
                    <select value={rule.optionId} onChange={(e) => updateRule(rule.id, { optionId: e.target.value })}
                      style={{ flex: '1 1 160px', padding: '8px 10px', borderRadius: 8, border: `1px solid #d8d9fb`, fontSize: 13 }}>
                      <option value="">Cevap seçiniz</option>
                      {opts.map((o) => <option key={o.id} value={o.id}>{o.text}</option>)}
                    </select>
                    <button type="button" onClick={() => removeRule(rule.id)} title="Kuralı sil"
                      style={{ border: `1px solid #f5d6db`, background: '#fff', color: '#c2455c', borderRadius: 8, padding: '0 12px', cursor: 'pointer' }}>
                      <Icon name="x" size={14} />
                    </button>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 5 }}>Alıcılar</div>
                  <RecipientChips items={rule.recipients || []} onChange={(r) => updateRule(rule.id, { recipients: r })} />
                </div>
              );
            })}
          </div>
        )}
      </Section>
    </div>
  );
}
